import { columnWidthVar } from 'components/tables/data-table/cssVars';

/**
 * Column geometry shared between Tier 1 (ExpensesGroupedTable) and Tier 2 (ExpensesGroupedTier2Table)
 * of the grouped Expenses view, so the per-currency columns line up vertically across tiers.
 *
 * Tier 1 runs on the v8 DataTable engine, which resolves managed column widths at runtime and
 * publishes each one as a `--utw-w-<column id>` CSS variable on the table container. Tier 2 renders
 * inside that container (a `colSpan`'d expanded row), so it inherits those variables and can pin its
 * own cells to them via `groupedMirroredWidth` — the two tables then track each other at any
 * viewport width, including after a drag-resize, with no shared magic numbers.
 *
 * The `*_WIDTH` fallbacks below only apply before Tier 1 has published its first measurement.
 *
 * The expand column is sized to seat the chevron icon as its own visually-distinct column with
 * room to breathe around the icon — it shouldn't read as a continuation of the rightmost amount
 * cell or get pinned against the table's right border.
 */
export const GROUPED_TYPE_COLUMN_WIDTH = '20%';
export const GROUPED_EXPAND_COLUMN_WIDTH = '64px';

/** Tier-1 column ids Tier 2 mirrors its widths from. */
export const GROUPED_TYPE_COLUMN_ID = 'expenseTypeCode';
export const GROUPED_EXPAND_COLUMN_ID = 'expand';
export const groupedAmountColumnId = (currencyCode: string): string => `amount-${currencyCode}`;

/**
 * Tier-1 `widthWeight` for the type column. The amount columns each carry weight 1, so weighting
 * the type column at a quarter of their count leaves it at 20% of the flex pool for ANY number of
 * currencies — a flat 20% share, without hard-coding a currency count.
 *
 * The floor applies to the COUNT, not to the resulting weight: clamping the weight itself would
 * hand the type column 50% / 33% / 25% for one / two / three currencies. With no currency columns
 * at all the type column is the only flex column, so its weight just has to be positive.
 */
export const groupedTypeColumnWeight = (currencyCount: number): number => Math.max(currencyCount, 1) / 4;

/**
 * Horizontal inset the expanded row's cell puts around Tier 2 on EACH side (the shared managed-cell
 * padding). Tier 2's containing block is therefore twice this narrower than Tier 1, so mirroring
 * Tier 1's widths verbatim would overflow the cell and push the trailing chevron outside the row.
 */
const TIER_2_CELL_INSET_PX = 16;

/** CSS width that tracks the Tier-1 column of the same id, falling back until Tier 1 has measured.
 *
 *  `edge` marks Tier 2's FIRST and LAST columns, which each give back one inset. That makes the
 *  widths sum to Tier 2's real containing block — so the table fits inside the inset — while every
 *  boundary BETWEEN columns still lands on its Tier-1 counterpart, which is what keeps the amount
 *  columns lined up vertically across the tiers. */
export const groupedMirroredWidth = (columnId: string, fallback: string, edge = false): string => {
    const tier1Width = `var(${columnWidthVar(columnId)}, ${fallback})`;
    return edge ? `calc(${tier1Width} - ${TIER_2_CELL_INSET_PX}px)` : tier1Width;
};
